import React from 'react';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import { PARAM, APP_PATHS, STUDENT_SUMMARY_PATHS } from '../../constants/shared/ApiConstants';
import { ENROLLMENT } from '../../constants/shared/CommonConstants';
import { selectors as enrollmentSelectors } from '../../api/enrollment/shared/enrollmentReducer';
import { selectors as studentSummarySelectors } from '../../api/studentSummary/shared/studentSummaryReducer';
import { printPage } from './printUtils';
import {
    ALL_PM_GRAPHS_PDF,
    ALL_STUDENT_SUMMARY_PDF,
    CLASS_SUMMARY_PDF,
    CURRENT_PAGE,
    DUAL_LANGUAGE_CLASS_SUMMARY_PDF,
    TRO_CLASS_SUMMARY_PDF,
} from '../../constants/shared/PdfGenerationConstants';

const { DISTRICT, SCHOOL, CLASS_AND_GROUP } = ENROLLMENT;
const { BENCHMARK, PROGRESS, STUDENT_SUMMARY } = APP_PATHS;
const { SS_BENCHMARK_HISTORY, SS_TRC_SUMMARY } = STUDENT_SUMMARY_PATHS;

export const getIsPrintIconDisabled = (
    isGradeYearsFetching,
    isPdfDownloading,
    isDataFetched,
    isDataReady,
) => isGradeYearsFetching || isPdfDownloading || !isDataFetched || !isDataReady;

export const getDistrictDropDownProps = ({ enrollment, studentSummary }) => ({
    items: enrollmentSelectors.getDistricts(enrollment),
    label: DISTRICT,
    urlName: PARAM.DISTRICT,
    chosenItem: enrollmentSelectors.getDistrictInfo(enrollment),
    isDisabled:
        isEmpty(enrollmentSelectors.getDistricts(enrollment)) ||
        enrollmentSelectors.getDistricts(enrollment).length <= 1 ||
        !isNil(studentSummarySelectors.getSelectedStudentId(studentSummary)),
});

export const getSchoolDropDownProps = ({ enrollment, studentSummary }) => ({
    items: enrollmentSelectors.getSchools(enrollment),
    label: SCHOOL,
    urlName: PARAM.SCHOOL,
    chosenItem: enrollmentSelectors.getSchoolInfo(enrollment),
    isDisabled:
        isEmpty(enrollmentSelectors.getSchools(enrollment)) ||
        enrollmentSelectors.getSchools(enrollment).length <= 1 ||
        !isNil(studentSummarySelectors.getSelectedStudentId(studentSummary)),
});

export const getClassDropDownProps = ({ enrollment, studentSummary }) => ({
    items: enrollmentSelectors.getClassesAndGroups(enrollment),
    label: CLASS_AND_GROUP,
    urlName: PARAM.CLASSE,
    chosenItem: enrollmentSelectors.getClassOrGroupInfo(enrollment),
    isDisabled:
        isEmpty(enrollmentSelectors.getClassesAndGroups(enrollment)) ||
        enrollmentSelectors.getClassesAndGroups(enrollment).length <= 1 ||
        !isNil(studentSummarySelectors.getSelectedStudentId(studentSummary)),
});

export const getPrintItemsWithHandler = (
    printItems,
    generatePdf,
    uploadPdfAvailability,
    fetchAllStudentsInfo,
    fetchAdditionalEnrollmentData,
    fetchAdditionalStudentResults,
    selectedPeriodLabel = '',
    fetchBooksInformation = () => {},
    lrsPrintCurrentPage = () => {},
) => {
    const HANDLER_MAPPER = {
        [CURRENT_PAGE]: e => {
            printPage(e);
            lrsPrintCurrentPage({
                pdfInHeader: true,
                documentKey: CURRENT_PAGE,
            });
        },
        [CLASS_SUMMARY_PDF]: e => {
            e.preventDefault();
            generatePdf({
                documentKey: CLASS_SUMMARY_PDF,
                fileName: `${CLASS_SUMMARY_PDF}-${selectedPeriodLabel}`,
                pdfInHeader: true,
            });
            uploadPdfAvailability(CLASS_SUMMARY_PDF);
        },
        [ALL_STUDENT_SUMMARY_PDF]: e => {
            e.preventDefault();
            fetchAllStudentsInfo(() => {
                generatePdf({
                    documentKey: ALL_STUDENT_SUMMARY_PDF,
                    fileName: ALL_STUDENT_SUMMARY_PDF,
                    pdfInHeader: true,
                });
                uploadPdfAvailability(ALL_STUDENT_SUMMARY_PDF);
            });
        },
        [ALL_PM_GRAPHS_PDF]: e => {
            e.preventDefault();
            generatePdf({
                documentKey: ALL_PM_GRAPHS_PDF,
                fileName: ALL_PM_GRAPHS_PDF,
                pdfInHeader: true,
            });
            uploadPdfAvailability(ALL_PM_GRAPHS_PDF);
        },
        [DUAL_LANGUAGE_CLASS_SUMMARY_PDF]: e => {
            e.preventDefault();
            generatePdf(
                {
                    documentKey: DUAL_LANGUAGE_CLASS_SUMMARY_PDF,
                    fileName: `${DUAL_LANGUAGE_CLASS_SUMMARY_PDF}-${selectedPeriodLabel}`,
                },
                async () => {
                    await fetchAdditionalEnrollmentData();
                    await fetchAdditionalStudentResults();
                },
            );
            uploadPdfAvailability(DUAL_LANGUAGE_CLASS_SUMMARY_PDF);
        },
        [TRO_CLASS_SUMMARY_PDF]: e => {
            e.preventDefault();
            fetchBooksInformation(() => {
                generatePdf({
                    documentKey: TRO_CLASS_SUMMARY_PDF,
                    fileName: `${TRO_CLASS_SUMMARY_PDF}-${selectedPeriodLabel}`,
                    pdfInHeader: true,
                });
                uploadPdfAvailability(TRO_CLASS_SUMMARY_PDF);
            });
        },
    };

    return printItems.map(item => ({
        ...item,
        onClick: HANDLER_MAPPER[item.id],
    }));
};

export const getTitlePdf = (title, isNew) =>
    isNew ? (
        <span>
            {title}
            <span className="new-item-label">NEW</span>
        </span>
    ) : (
        title
    );

export const getTopMenuItems = (
    licensingInfo,
    location,
    isDisabled,
    buildHelpItems,
    availablePrintItems,
    lrsSelectHelpItem,
    isCurrentPagePrint = false,
) => [
    {
        iconClassName: 'icon-amplify-print',
        isDisabled,
        countOfNewItems: !isDisabled && availablePrintItems.filter(({ isNew }) => isNew).length,
        ...(isCurrentPagePrint
            ? { onClick: printPage }
            : {
                  dropDownItems: availablePrintItems,
              }),
    },
    {
        iconClassName: 'icon-amplify-help',
        dropDownItems: buildHelpItems(licensingInfo, location, lrsSelectHelpItem),
    },
];

export const getPageActiveClass = ({ pathname }, viewKey) => {
    const selectedTabClassName = 'active';
    const notSelectedTabClasName = '';
    const SELECTED_TABS_CONFIG = {
        [BENCHMARK]: [SS_BENCHMARK_HISTORY, SS_TRC_SUMMARY],
        [PROGRESS]: [PROGRESS],
    };
    const isTabSelected = pathname.includes(STUDENT_SUMMARY)
        ? SELECTED_TABS_CONFIG[viewKey]?.some(tab => pathname.includes(tab))
        : pathname.includes(viewKey);

    return isTabSelected ? selectedTabClassName : notSelectedTabClasName;
};
